import React from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { useExport } from '@/hooks/useExport';
import { useToast } from '@/components/ui/use-toast';
import { Transaction } from '@/context/FinanceContext';

interface ColumnConfig {
  key: string;
  title: string;
  width: number;
  visible: boolean;
}

interface ExportButtonsProps {
  transactions: Transaction[];
  categories: { id: string; name: string }[];
  accounts: { id: string; name: string }[];
  companies: { id: string; name: string }[];
  paymentMethods: { id: string; name: string }[];
  costCenters: { id: string; name: string }[];
  suppliers?: { id: string; name: string }[];
  selectedTransactions?: Transaction[];
  disabled?: boolean;
}

export const ExportButtons: React.FC<ExportButtonsProps> = ({
  transactions,
  categories,
  accounts,
  companies,
  paymentMethods,
  costCenters,
  suppliers = [],
  selectedTransactions,
  disabled = false
}) => {
  const { exportToExcel, exportToPDF } = useExport();
  const { toast } = useToast();

  const getColumnConfiguration = (): ColumnConfig[] | undefined => {
    try {
      const saved = localStorage.getItem('optimized-excel-table-columns');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load column configuration:', error);
    }
    return undefined;
  };

  const dataToExport = selectedTransactions && selectedTransactions.length > 0 
    ? selectedTransactions 
    : transactions;

  const exportData = {
    transactions: dataToExport,
    categories,
    accounts,
    companies,
    paymentMethods,
    costCenters,
    suppliers
  };

  const handleExportExcel = async () => {
    const columnConfig = getColumnConfiguration();
    const filename = selectedTransactions && selectedTransactions.length > 0 
      ? 'lancamentos_selecionados' 
      : 'lancamentos';
    
    const result = await exportToExcel(exportData, filename, columnConfig);
    toast({
      title: result.success ? "Sucesso" : "Erro",
      description: result.message,
      variant: result.success ? "default" : "destructive"
    });
  };

  const handleExportPDF = () => {
    const columnConfig = getColumnConfiguration();
    const filename = selectedTransactions && selectedTransactions.length > 0 
      ? 'lancamentos_selecionados' 
      : 'lancamentos';
    
    const result = exportToPDF(exportData, filename, columnConfig);
    toast({
      title: result.success ? "Sucesso" : "Erro", 
      description: result.message,
      variant: result.success ? "default" : "destructive"
    });
  };

  if (dataToExport.length === 0) {
    return (
      <Button variant="outline" size="sm" disabled>
        <Download className="h-4 w-4 mr-2" />
        Exportar (0 registros)
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Download className="h-4 w-4 mr-2" />
          Exportar ({dataToExport.length})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleExportExcel}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Exportar para Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportPDF}>
          <FileText className="h-4 w-4 mr-2" />
          Exportar para PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};